import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuthStore} from "@store/AuthStore";
import {AppRoute} from "@router/RoutingModels";
import {buildRoute} from "@router/RouterUtils";
import {useShallow} from "zustand/react/shallow";
import {getValidAccessToken} from "@msal/tokenService";
import {usePrivateBootstrap} from "@lib/Bootstrap/usePrivateBootstrap";
import {resetI18nOnLogout} from "@i18n/project/reset";
import {useQueryClient} from "@tanstack/react-query";
import {cleanupRejectedMsalAccount} from "@msal/msalUtils";

function isMsalAuthResponseUrl(url: URL): boolean {
    const hasQueryCode = url.searchParams.has("code") && url.searchParams.has("state");
    const hash = url.hash || "";
    const hasHashTokens = /id_token=|access_token=|code=/.test(hash);
    return hasQueryCode || hasHashTokens;
}

type SsoState =
    | "checking"
    | "waiting_auth"
    | "bootstrapping"
    | "redirecting";

export default function SsoPage() {

    const queryClient = useQueryClient();

    const {isAuthenticated, derivedCountry, setOverlay, setOverlayMessage} = useAuthStore(
        useShallow((s) => ({
            isAuthenticated: s.isAuthenticated,
            derivedCountry: s.derivedCountry,
            setOverlay: s.setOverlay,
            setOverlayMessage: s.setOverlayMessage
        }))
    );

    const {run: runBootstrap} = usePrivateBootstrap();
    const navigate = useNavigate();
    const [state, setState] = useState<SsoState>("checking");

    const ranRef = useRef(false);

    useEffect(() => {
        const run = async () => {

            console.log("Start return handle from b2c");

            resetI18nOnLogout(queryClient);

            if (ranRef.current) return;
            ranRef.current = true;

            console.log("Handling...")
            setOverlay(true);
            setOverlayMessage("Loading session...");

            const url = new URL(window.location.href);
            const isCallback = isMsalAuthResponseUrl(url);

            if (!isCallback && !isAuthenticated) {
                console.log("Non callback + non autenticato")
                setOverlayMessage("Redirecting...");
                setState("redirecting");
                navigate(buildRoute(AppRoute.WELCOME), {replace: true});
                return;
            }

            if (isCallback && !isAuthenticated) {
                console.log("Callback ma MSAL non ha ancora completato")
                setOverlayMessage("Processing login... (this may take a few seconds)");
                setState("waiting_auth");
                return;
            }

            setOverlayMessage("Initializing...");

            setState("bootstrapping");

            const token = await getValidAccessToken(false);
            const result = await runBootstrap({token, b2cCountry: derivedCountry});

            if (!result.ok) {
                console.log("!result.ok")
                setState("redirecting");

                if (result.reason === "NOT_AUTHENTICATED") {
                    console.log("NOT_AUTHENTICATED")
                    navigate(buildRoute(AppRoute.WELCOME), {replace: true});
                    return;
                }

                if (result.reason === "ME_USER_MISSING") {
                    console.log("ME_USER_MISSING")
                    navigate(buildRoute(AppRoute.ON_BOARD), {replace: true});
                    return;
                }

                if (result.reason === "COUNTRY_DISABLED" || result.reason === "COUNTRY_MISMATCH") {
                    await cleanupRejectedMsalAccount();
                    navigate(`${buildRoute(AppRoute.OPS)}?reason=${encodeURIComponent(result.reason)}`, {
                        replace: true,
                        state: {reason: result.reason},
                    });
                    return;
                }

                console.log("result.reason n/a", result.reason)
                navigate(`${buildRoute(AppRoute.OPS)}?reason=${encodeURIComponent(result.reason)}`, {
                    replace: true,
                    state: {reason: result.reason},
                });
                return;
            }

            const saved = sessionStorage.getItem("auth:returnUrl");
            if (saved) sessionStorage.removeItem("auth:returnUrl");

            navigate(buildRoute(AppRoute.HOME), {replace: true});
        };

        run().then();
    }, [isAuthenticated, derivedCountry, navigate, setOverlay, runBootstrap, queryClient]);

    useEffect(() => {
        const url = new URL(window.location.href);
        const isCallback = isMsalAuthResponseUrl(url);
        if (isCallback && isAuthenticated) {
            ranRef.current = false;
        }
    }, [isAuthenticated]);

    return (
        <div style={{textAlign: "center", marginTop: "20vh"}}>
            {state === "checking" && <h1>Inizializzazione…</h1>}
            {state === "waiting_auth" && <h1>Elaborazione login…</h1>}
            {state === "bootstrapping" && <h1>Configurazione contesto…</h1>}
            {state === "redirecting" && <h1>Reindirizzamento…</h1>}
        </div>
    );
}
